/**
 * The Lakshi assistant's API client.
 *
 * CONTRACT SOURCE: the M71 assistant contract in the API repository. The paths below
 * are relative to the transport's base URL (`/api/v1`), so `/ai/assistant/context`
 * here is `/api/v1/ai/assistant/context` on the wire.
 *
 * ---------------------------------------------------------------------------
 * TWO FAMILIES, ONE CLIENT
 * ---------------------------------------------------------------------------
 * The API exposes the assistant twice: under `/ai/assistant/*` for a tenant the
 * session resolves to, and under `/ai/platform/assistant/*` for the deployment's own
 * pages. The base path is chosen by the SCOPE, and the scope is decided in exactly
 * one place — `assistant-scope.ts` — so a caller never assembles a path itself.
 *
 * ---------------------------------------------------------------------------
 * A TURN IS NOT IDEMPOTENT
 * ---------------------------------------------------------------------------
 * The contract marks these operations as NOT requiring an idempotency key, and none
 * is sent: a repeated turn is a second answer from the provider, not the same write
 * replayed. Retries are off in the query client, so a turn is sent once.
 */
import { http } from '@/api/client/http';
import {
  AssistantContextResponseSchema,
  AssistantMessageListResponseSchema,
  AssistantThreadListResponseSchema,
  AssistantThreadResponseSchema,
  AssistantTurnResponseSchema,
} from '@/api/schemas/assistant-responses';
import type {
  AssistantContext,
  AssistantContextInput,
  AssistantMessage,
  AssistantMessageInput,
  AssistantThread,
  AssistantThreadInput,
  AssistantTurn,
} from '@/api/schemas/assistant';
import type { AssistantScope } from '@/modules/assistant/services/assistant-scope';

/**
 * The base path of each family.
 *
 * The platform family's path is the tenant family's path with `/platform` in
 * front of it — the same shape the AI module already uses for `/ai` and
 * `/ai/platform`, so the two modules read alike.
 */
const ASSISTANT_BASE_BY_SCOPE: Record<AssistantScope, string> = {
  tenant: '/ai/assistant',
  platform: '/ai/platform/assistant',
};

export const assistantApi = {
  /**
   * The route-aware context: who the agent is, what this page can do, the
   * prefilled suggestions, and the sanitised facts.
   */
  async getContext(scope: AssistantScope, input: AssistantContextInput): Promise<AssistantContext> {
    const response = await http.get(
      `${ASSISTANT_BASE_BY_SCOPE[scope]}/context`,
      AssistantContextResponseSchema,
      { query: { route: input.route } },
    );
    return response.data;
  },

  /** The caller's conversations. */
  async listThreads(scope: AssistantScope): Promise<AssistantThread[]> {
    const response = await http.get(
      `${ASSISTANT_BASE_BY_SCOPE[scope]}/threads`,
      AssistantThreadListResponseSchema,
    );
    return response.data.items;
  },

  /** Opens a conversation, anchored to a route. */
  async createThread(scope: AssistantScope, input: AssistantThreadInput): Promise<AssistantThread> {
    const response = await http.post(
      `${ASSISTANT_BASE_BY_SCOPE[scope]}/threads`,
      AssistantThreadResponseSchema,
      { body: input },
    );
    return response.data;
  },

  /** One conversation's turns. */
  async listMessages(scope: AssistantScope, threadId: string): Promise<AssistantMessage[]> {
    const response = await http.get(
      `${ASSISTANT_BASE_BY_SCOPE[scope]}/threads/${threadId}/messages`,
      AssistantMessageListResponseSchema,
    );
    return response.data.items;
  },

  /**
   * Sends one turn and returns the answer.
   *
   * The answer may carry a DRAFT. A draft is data for a form to review — this call
   * writes nothing, and the returned draft is handed to the console to show.
   */
  async sendMessage(
    scope: AssistantScope,
    threadId: string,
    input: AssistantMessageInput,
  ): Promise<AssistantTurn> {
    const response = await http.post(
      `${ASSISTANT_BASE_BY_SCOPE[scope]}/threads/${threadId}/messages`,
      AssistantTurnResponseSchema,
      { body: input },
    );
    return response.data;
  },
};
